import type { ReactElement } from 'react';

import { useI18n } from '#/i18n/hooks/useI18n';
import type { ApiVolopPromotionType } from '#/modules/listing/volops/api/types/ApiVolopPromotionType';
import type { SearchAlgoliaHit } from '#/modules/search/algolia/types/SearchAlgoliaHit';
import { SearchEventHit } from '#/modules/search/events/components/Hit/SearchEventHit';
import { SearchImportedHit } from '#/modules/search/imported/components/Hit/SearchImportedHit';
import { SearchInternshipHit } from '#/modules/search/internships/components/Hit/SearchInternshipHit';
import { SearchJobHit } from '#/modules/search/jobs/components/Hit/SearchJobHit';
import { SearchOrgHit } from '#/modules/search/orgs/components/Hit/SearchOrgHit';
import { SearchVolopHit } from '#/modules/search/volops/components/Hit/SearchVolopHit';
import type { SearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import type { DatetimeString } from '#/types/TypedDateString';

type Props = {
  hit: SearchAlgoliaHit;
  trackClick: (() => void) | null;
  showLabel: boolean;
  /**
   * Providing `null` hides the save button
   */
  searchSavedListingsStore: SearchSavedListingsStore | null;
  highlighted: boolean;
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: DatetimeString;
  } | null;
  actions: ReactElement | null;
  promotionType: ApiVolopPromotionType | null;
  showOrgLink?: boolean;
};

export function SearchHit({
  hit,
  searchSavedListingsStore,
  highlighted,
  application,
  actions,
  trackClick,
  showLabel,
  promotionType,
  showOrgLink,
}: Props) {
  const { locale } = useI18n();

  switch (hit.type) {
    case 'JOB':
      return (
        <SearchJobHit
          hit={hit}
          href={hit.url[locale]}
          searchSavedListingsStore={searchSavedListingsStore}
          highlighted={highlighted}
          application={application}
          actions={actions}
          trackClick={trackClick}
          showLabel={showLabel}
        />
      );
    case 'INTERNSHIP':
      return (
        <SearchInternshipHit
          hit={hit}
          href={hit.url[locale]}
          searchSavedListingsStore={searchSavedListingsStore}
          highlighted={highlighted}
          application={application}
          actions={actions}
          trackClick={trackClick}
          showLabel={showLabel}
        />
      );
    case 'VOLOP':
      return (
        <SearchVolopHit
          hit={hit}
          href={hit.url[locale]}
          searchSavedListingsStore={searchSavedListingsStore}
          highlighted={highlighted}
          application={application}
          actions={actions}
          trackClick={trackClick}
          showLabel={showLabel}
          promotionType={promotionType}
          showOrgLink={showOrgLink}
        />
      );
    case 'EVENT':
      return (
        <SearchEventHit
          hit={hit}
          href={hit.url[locale]}
          searchSavedListingsStore={searchSavedListingsStore}
          highlighted={highlighted}
          application={application}
          trackClick={trackClick}
          showLabel={showLabel}
        />
      );
    case 'IMPORTED':
      return (
        <SearchImportedHit
          hit={hit}
          highlighted={highlighted}
          application={application}
          actions={actions}
          trackClick={trackClick}
          showLabel={showLabel}
        />
      );
    case 'ORG':
      return (
        <SearchOrgHit
          hit={hit}
          href={hit.url[locale]}
          searchSavedListingsStore={searchSavedListingsStore}
          highlighted={highlighted}
          trackClick={trackClick}
          showLabel={showLabel}
        />
      );
  }
}
